import React, { useEffect, useMemo, useState } from "react";
import { Download, Calendar, FileText, Filter, ChevronDown, RefreshCw, Eye, Loader, Printer } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import "../MesrequetesStyles/BulletinsPaie.css";
import "../MesrequetesStyles/MesOrdresShared.css";
import { API_BASE_URL, getBlobRequest } from "../../../../../api";
import { fetchMyPayslipsRequest } from "../../../../redux/actions/payslipActions";

const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value, locale) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString(locale);
};

const splitPeriod = (periodLabel, importedAt, locale, notDetected) => {
    if (periodLabel) {
        const parts = periodLabel.split(" ");
        if (parts.length >= 2) {
            return { month: parts.slice(0, -1).join(" "), year: parts[parts.length - 1] };
        }
    }

    if (importedAt) {
        const date = new Date(importedAt);
        if (!Number.isNaN(date.getTime())) {
            return {
                month: date.toLocaleString(locale, { month: "long" }).replace(/^./, (value) => value.toUpperCase()),
                year: String(date.getFullYear()),
            };
        }
    }

    return { month: notDetected, year: "" };
};

export default function BulletinsPaie() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { myPayslips, loading, error } = useSelector((state) => state.payslips);
    const [searchMonth, setSearchMonth] = useState("");
    const [searchYear, setSearchYear] = useState("");
    const [showFilters, setShowFilters] = useState(true);

    useEffect(() => {
        dispatch(fetchMyPayslipsRequest());
    }, [dispatch]);

    const bulletins = useMemo(() => (myPayslips || []).map((item) => {
        const period = splitPeriod(item.periodLabel, item.importedAt, i18n.language, t("Non détectée"));
        return { ...item, month: period.month, year: period.year, displayDate: formatDate(item.importedAt, i18n.language), displaySize: formatSize(item.fileSize) };
    }), [myPayslips, i18n.language, t]);

    const mois = useMemo(() => [...new Set(bulletins.map((item) => item.month).filter(Boolean))], [bulletins]);
    const annees = useMemo(() => [...new Set(bulletins.map((item) => item.year).filter(Boolean))].sort((a, b) => Number(b) - Number(a)), [bulletins]);
    const bulletinsFiltres = useMemo(() => bulletins.filter((item) => (
        (!searchMonth || item.month === searchMonth) && (!searchYear || item.year === searchYear)
    )), [bulletins, searchMonth, searchYear]);

    const resetFilters = () => {
        setSearchMonth("");
        setSearchYear("");
    };

    const openPdf = async (relativeUrl, fileName, download = false, print = false) => {
        try {
            const response = await getBlobRequest({
                url: `${API_BASE_URL}${relativeUrl}${download ? "?download=true" : ""}`,
                token: sessionStorage.getItem("token"),
                extraHeaders: { uid: sessionStorage.getItem("uid") },
            });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));

            if (download) {
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = fileName || "bulletin.pdf";
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
                return;
            }

            if (print) {
                const printFrame = document.createElement("iframe");
                // An iframe with display:none is not printable in several browsers.
                // Keep it rendered, but outside the visible page, until printing ends.
                printFrame.style.position = "fixed";
                printFrame.style.right = "0";
                printFrame.style.bottom = "0";
                printFrame.style.width = "1px";
                printFrame.style.height = "1px";
                printFrame.style.border = "0";
                printFrame.style.opacity = "0";
                printFrame.setAttribute("aria-hidden", "true");
                printFrame.src = blobUrl;
                printFrame.onload = () => {
                    const cleanup = () => {
                        printFrame.remove();
                        window.URL.revokeObjectURL(blobUrl);
                    };
                    const printWindow = printFrame.contentWindow;
                    if (!printWindow) {
                        cleanup();
                        return;
                    }
                    printWindow.onafterprint = cleanup;
                    // Wait for the browser PDF viewer to finish rendering the document.
                    setTimeout(() => {
                        printWindow.focus();
                        printWindow.print();
                    }, 500);
                    // Fallback when the browser does not raise onafterprint.
                    setTimeout(cleanup, 30000);
                };
                document.body.appendChild(printFrame);
                return;
            }

            window.open(blobUrl, "_blank", "noopener,noreferrer");
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
        } catch (downloadError) {
            Swal.fire({
                icon: "error",
                title: t("Téléchargement impossible"),
                text: t("Le bulletin de paie n'a pas pu être récupéré."),
                confirmButtonText: t("OK"),
            });
        }
    };

    return (
        <div className="bpPage mesOrdresStyle mesOrdresPayslips">
            <div className="bpWrap">
                <header className="bpHeader">
                    <div>
                        <h1>{t("Mes bulletins de paie")}</h1>
                        <p>{t("Consultez et téléchargez vos bulletins de paie.")}</p>
                    </div>
                </header>

                <section className="bpCard">
                    <div className="bpCardHeader">
                        {/*<div className="bpCardTitle"><FileText size={16} /> {t("Historique des bulletins")}</div>*/}
                        <button type="button" className="bpFilterBtn" onClick={() => setShowFilters((value) => !value)}>
                            <Filter size={15} /> {t("Filtres")} <ChevronDown size={15} className={`bpChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="bpFilters">
                            <div className="bpField">
                                <label>{t("Mois")}</label>
                                <div className="bpSelectWrap">
                                    <Calendar size={15} />
                                    <select value={searchMonth} onChange={(event) => setSearchMonth(event.target.value)}>
                                    <option value="">{t("Tous les mois")}</option>
                                        {mois.map((month) => <option key={month} value={month}>{month}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="bpField">
                                <label>{t("Année")}</label>
                                <div className="bpSelectWrap">
                                    <Calendar size={15} />
                                    <select value={searchYear} onChange={(event) => setSearchYear(event.target.value)}>
                                    <option value="">{t("Toutes les années")}</option>
                                        {annees.map((year) => <option key={year} value={year}>{year}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="bpField bpField--actions">
                                <label>&nbsp;</label>
                                <button type="button" className="bpResetBtn" onClick={resetFilters}><RefreshCw size={15} /> {t("Réinitialiser")}</button>
                            </div>
                        </div>
                    )}

                    {(searchMonth || searchYear) && (
                        <div className="bpActiveFilters">
                            <span>{t("Filtres actifs :")}</span>
                            {searchMonth && <span className="bpTag">{searchMonth}</span>}
                            {searchYear && <span className="bpTag">{searchYear}</span>}
                        </div>
                    )}
                </section>

                <section className="bpCard bpCard--noPad">
                    <div className="bpListHeader">
                        <h2>{t("Liste des bulletins de paie")}</h2>
                        {bulletins.length > 0 && <span className="bpCountBadge">{bulletinsFiltres.length} {t(bulletinsFiltres.length > 1 ? "bulletins" : "bulletin")}</span>}
                    </div>

                    {loading && <div className="bpEmpty"><Loader size={28} className="spin" /><p>{t("Chargement des bulletins de paie…")}</p></div>}
                    {!loading && error && <div className="bpEmpty"><FileText size={28} /><p>{error}</p></div>}
                    {!loading && !error && bulletinsFiltres.length === 0 && <div className="bpEmpty"><FileText size={28} /><p>{t("Aucun bulletin de paie disponible")}</p></div>}

                    {!loading && !error && bulletinsFiltres.length > 0 && (
                        <div className="bpTableWrap">
                            <table className="bpTable">
                                <thead><tr><th>{t("Période")}</th><th>{t("Fichier")}</th><th>{t("Ajouté le")}</th><th>{t("Taille")}</th><th aria-label={t("Actions")} /></tr></thead>
                                <tbody>
                                    {bulletinsFiltres.map((bulletin, index) => (
                                        <tr key={bulletin.id || bulletin.downloadUrl || index}>
                                            <td><div className="bpPeriod"><span className="bpPeriodIcon"><FileText size={14} /></span><strong>{bulletin.periodLabel || `${t("Bulletin de")} ${bulletin.month} ${bulletin.year}`}</strong></div></td>
                                            <td className="bpFileName">{bulletin.fileName || bulletin.sourceFileName || t("Bulletin.pdf")}</td>
                                            <td>{bulletin.displayDate}</td>
                                            <td><span className="bpBadge">{bulletin.displaySize}</span></td>
                                            <td className="bpActions">
                                                {/*<button type="button" className="bpIconBtn" title="Voir le bulletin" aria-label="Voir le bulletin" onClick={() => openPdf(bulletin.downloadUrl, bulletin.fileName, false)}><Eye size={14} /></button>*/}
                     <button type="button" className="bpIconBtn bpIconBtn--primary" title={t("Télécharger le bulletin")} aria-label={t("Télécharger le bulletin")} onClick={() => openPdf(bulletin.downloadUrl, bulletin.fileName, true)}><Download size={14} /></button>
                     <button type="button" className="bpIconBtn" title={t("Imprimer le bulletin")} aria-label={t("Imprimer le bulletin")} onClick={() => openPdf(bulletin.downloadUrl, bulletin.fileName, false, true)}><Printer size={14} /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
